import Styles from './TaskGroup.scss';

import Template from './TaskGroup.hbs';
import Util from 'util/util';
import TaskView from 'views/components/taskGroups/tasks/Task';
import SourceView from 'views/components/taskGroups/sources/Source';
import Button from '../button/Button';
import SidebarLearningText from 'views/pages/activities/show/sidebars/learningText/LearningText';
import TheorySourcesCollection from 'collections/TheorySourcesCollection';
import TextToSpeech from 'views/components/taskGroups/TextToSpeech.svelte';
import AudioUtil from 'util/AudioUtil';
import LanguageSpecificCharacters from '../languageSpecificCharacters/LanguageSpecificCharacters.svelte';

const splitLayoutBreakPointInPx = 1200;
const splitMinLeftColumnPercent = 40;
const splitMaxLeftColumnPercent = 60;

export default class TaskGroup extends BaseView {

    /**
     * initialize
     *
     * this.model (TaskGroupModel)                  Model containing information on the task group.
     *
     * @param {BaseView} work_on                    Required parent view.
     * @param {boolean|undefined} isPreview         If true, parent activity is in preview mode, meaning it's
     *                                              read-only and answers cannot be entered or modified.
     * @param {boolean|undefined} hasLinkToTask     If true, clicking the task group index or child task index
     *                                              navigates to the same task group/tasks in the context of its
     *                                              activity/task group in the 'show' view.
     * @param {boolean|undefined} hideSources       If true, skip the rendering of source elements.
     * @param {boolean|undefined} onlyShowAnswer    If true, do not render task content, only student answer view.
     * @param {number|undefined} singleAnswerUserId  If set , only show answer of a single student instead of group.
     *
     */
    initialize({
        work_on,
        isPreview,
        hasLinkToTask,
        hideSources,
        onlyShowAnswer,
        singleAnswerUserId,
        isExportingExamAnswers
    }) {

        _.bindAll(
            this,
            'onClickNavigateToTaskGroup',
            'showExplanation',
            'onChangeSplitLayoutMediaQuery',
            'onResizeKnobPointerDown',
            'onResizeKnobPointerMove',
            'onResizeKnobPointerUp'
        );

        // If sources are hidden and there are no tasks, abort rendering this task group view.
        if (hideSources && this.model.tasks.length === 0) {
            return
        }

        const activityModel = this.model.getActivityModel();
        const activityType = activityModel?.get('type')

        // When navigating from author to "lesmateriaal-view" the pathname is: /task_groups/show
        const hasTextToSpeech = !hasLinkToTask &&
            activityType !== 'presentation' &&
            Util.stripTags(this.model.get('introduction')).length > 10 &&
            (window.location.pathname.startsWith('/activities/show') || window.location.pathname.startsWith('/task_groups/show'))

        // Check if user is actually inside a presentation and is not loading a
        // taskgroup belonging to a presentation. F.e. when checking students answers.
        const isPresentation = activityType === 'presentation' &&
            /^Presentation/.test(work_on?.el.classList.value);

        // Pre-compute split-layout eligibility from the raw element data so the
        // template can skip rendering the split scaffolding entirely for taskgroups
        // that will never enter split mode.
        const sourceElements = this.model.elements.filter(
            el => el.get('element_type') === 'source' && el.get('prefer_left_position')
        );

        const splitEligible = this.model.getActivityModel()?.hasSupportForTwoColumnView()
            && !hideSources
            && sourceElements.length > 0
            && this.model.hasSupportForTwoColumnView()

        this.setElement(Template({
            Styles,
            index: this.model.get('index'),
            introduction: Util.renderContentSafely(this.model.get('introduction')),
            isPresentation,
            splitEligible,
            hasLinkToTask,
            hasTextToSpeech,
            contentDomain: this.model.getContentDomainItem()?.name
        }));

        this.taskGroupElements = this.$('.js-task-group-elements');
        var taskGroupOptionalElements = this.$('.js-task-group-optional-elements');

        var taskViews = [];

        // Show button for explanation attached to this taskgroup
        // Only show it in supported activity types and when on activity/show page (and not in progress or library)
        const explanationLabel = this.model.getExplanationLabel(true)
        if (explanationLabel &&
            ['linear', 'diagnostic_exam', 'adaptive', 'adaptive_student'].includes(activityType) &&
            work_on?.el.closest('.js-activity-show')
        ) {
            this.addChildView(
                new Button({
                    label: explanationLabel,
                    icon: 'learning-text',
                    inline: true,
                    callback: this.showExplanation
                }),
                '.js-explanation-button'
            )
        }

        // If no optional/additional elements are found, empty the optional materials element.
        const optionalElement = this.model.elements.findWhere({
            is_additional: true
        })
        if (!optionalElement || optionalElement.get('element_type') === 'source' && hideSources === true) {
            taskGroupOptionalElements.empty();
        }

        if (hasTextToSpeech) {
            this.addSvelteChildView(
                '.js-text-to-speech',
                TextToSpeech,
                {
                    url: `/task_groups/synthesize/${this.model.id}.json`,
                    showCallback: () => {
                        this.$('.js-taskgroup-index-container').addClass(Styles.text_to_speech_active);
                    },
                    hideCallback: () => {
                        this.$('.js-taskgroup-index-container').removeClass(Styles.text_to_speech_active);
                    }
                }
            )
        } else if (hasLinkToTask) {
            this.$('.js-taskgroup-index-container').on(
                'click',
                this.onClickNavigateToTaskGroup
            )
        }

        // Create a sources collection using a deep clone of all the elements to prevent changing the input data.
        // TODO: in the future consider using https://developer.mozilla.org/en-US/docs/Web/API/structuredClone
        // for all instances where we need to do a deep clone of something. The JSON method is "lossy" since
        // it converts only JSON-compatible data types. Data types such as Date, Infinity, Map, Set, undefined, etc.
        // are converted to null. See discussion https://stackoverflow.com/a/122704
        const theoryCollection = new TheorySourcesCollection(
            JSON.parse(JSON.stringify(this.model.elements.toJSON())),
            {parse: true}
        )

        for (const elementModel of this.model.elements.models) {

            let elementView

            if (elementModel.get('element_type') === 'task') {

                elementView = new TaskView({
                    model: elementModel,
                    work_on,
                    onlyShowAnswer,
                    activityModel: this.model.getActivityModel(),
                    hasLinkToTask,
                    singleAnswerUserId,
                    isPreview,
                    isExportingExamAnswers,
                    canBeEdited: this.model.canBeEdited(),
                })
                taskViews.push(elementView)

            } else if (!hideSources && elementModel.get('element_type') === 'source') {

                const mergedSourceModel = theoryCollection.find((sourceModel) => {
                    return sourceModel.get('mergedSourceIds')[0] === elementModel.id
                })
                if (!mergedSourceModel) {
                    continue
                }

                elementView = new SourceView({
                    model: mergedSourceModel,
                    work_on,
                    activityModel: this.model.getActivityModel(),
                    isPreview,
                })

            }

            if (elementView) {
                this.registerChildView(elementView)
                if (elementModel.get('is_additional')) {
                    elementView.appendTo(taskGroupOptionalElements)
                } else {
                    elementView.appendTo(this.taskGroupElements)
                }
            }

        }

        if (activityType === 'presentation' && this.model.elements.length > 1) {
            this.taskGroupElements.addClass(Styles['has-multiple-slide-elements'])
        }

        this.taskViews = taskViews;

        // Show demo user the tooltip pointing to menu progress button
        if (Backbone.Model.user.get('is_demo')) {
            this.tourToolTip = Backbone.View.layout.addTourTooltip({
                message: window.i18n.gettext('Go to the progress overview'),
                target: '.js-default-menu [href="/groups/progress"]',
                targetIsFixed: true
            });
        }

        const courseId = activityModel?.getGroupModel()?.get('course_id');

        const courseLangMap = {
            4: 'de',
            11: 'es',
            18: 'fr',
        }

        if (courseLangMap[courseId] && !isPreview) {
            this.addSvelteChildView(
                document.body,
                LanguageSpecificCharacters, {
                    language: courseLangMap[courseId],
                }
            );
        }

        if (splitEligible) {
            // Cache the source views we actually need to move so we don't have to
            // re-walk the child view list on every media-query change. The per-source
            // `prefer_left_position` filter happens once here, removing the need for
            // putSourceLeft to repeat the check.
            this.leftPreferringSourceViews = this.getChildViewsOfType(SourceView)
                .filter(source => source.model.get('prefer_left_position'));
            this.splitLayoutPlaceholders = new Map();
            this.splitLayoutMediaQuery = window.matchMedia(`(min-width: ${splitLayoutBreakPointInPx}px)`);
            this.splitLayoutMediaQuery.addEventListener('change', this.onChangeSplitLayoutMediaQuery);

            this.initResizeKnob();
            this.onChangeSplitLayoutMediaQuery({ matches: this.splitLayoutMediaQuery.matches });

            // Force adding the data-split attribute to the element. This lets the
            // rest of the page know it should render in 2 column, but determine it based
            // on device width. If this is not set, it will render the page as if
            // the two-column feature has never existed.
            this.el.toggleAttribute('data-split', true);
        }
    }

    onChangeSplitLayoutMediaQuery({ matches }) {

        this.el.toggleAttribute('data-split-wide', matches);

        if (matches) {
            this.leftPreferringSourceViews.forEach(source => this.putSourceLeft(source));
        } else {
            this.leftPreferringSourceViews.forEach(source => this.unsplitLeftSource(source));
        }
    }

    initResizeKnob() {
        this.resizeKnob = this.el.querySelector('.js-resize-knob');
        if (!this.resizeKnob) {
            return;
        }
        this.leftColumn = this.el.querySelector('.js-left-column');
        this.rightColumn = this.el.querySelector('.js-right-column');

        this.resizeKnob.addEventListener('pointerdown', this.onResizeKnobPointerDown);
    }

    onResizeKnobPointerDown(e) {
        if (!e.isPrimary) {
            return
        }

        e.preventDefault();

        const knobRect = this.resizeKnob.getBoundingClientRect();
        const leftRect = this.leftColumn.getBoundingClientRect();
        const rightRect = this.rightColumn.getBoundingClientRect();

        // Store data in the this scope so we can reuse them continuesly when
        // dragging the knob without recalculating.
        this.resizeContext = {
            trackLeft: leftRect.left,
            trackRight: rightRect.right,
            grabOffset: e.clientX - (knobRect.left + knobRect.width / 2),
            pointerId: e.pointerId
        };

        // To keep the knob look like it's hovered, even when the cursor leaves it (while pointer down)
        // add a special class. This can occur because the Y position of the knob is locked. So if you hold
        // the pointer down and move up and left, the :hover selector will be lost.
        this.resizeKnob.classList.add(Styles.resizing);

        this.resizeKnob.setPointerCapture(e.pointerId);
        this.resizeKnob.addEventListener('pointermove', this.onResizeKnobPointerMove);
        this.resizeKnob.addEventListener('pointerup', this.onResizeKnobPointerUp);
        this.resizeKnob.addEventListener('pointercancel', this.onResizeKnobPointerUp);
    }

    onResizeKnobPointerMove(e) {

        const { trackLeft, trackRight, grabOffset } = this.resizeContext;
        const dividerX = e.clientX - grabOffset;

        const totalWidth = trackRight - trackLeft;
        const rawPercent = ((dividerX - trackLeft) / totalWidth) * 100;

        // Make sure we never exceed the min 33% / max 66% threshold.
        const leftPercent = Math.max(
            splitMinLeftColumnPercent,
            Math.min(splitMaxLeftColumnPercent, rawPercent)
        );

        this.el.style.setProperty('--left-column-width', `${leftPercent}%`)
    }

    onResizeKnobPointerUp() {
        this.resizeKnob.classList.remove(Styles.resizing);

        if (this.resizeContext && this.resizeKnob.hasPointerCapture(this.resizeContext.pointerId)) {
            this.resizeKnob.releasePointerCapture(this.resizeContext.pointerId);
        }

        this.resizeKnob.removeEventListener('pointermove', this.onResizeKnobPointerMove);
        this.resizeKnob.removeEventListener('pointerup', this.onResizeKnobPointerUp);
        this.resizeKnob.removeEventListener('pointercancel', this.onResizeKnobPointerUp);

        this.resizeContext = null;
    }

    putSourceLeft(source) {

        if (this.splitLayoutPlaceholders.has(source.el)) {
            return;
        }
        if (this.leftColumn.contains(source.el)) {
            return;
        }

        // Insert a placeholder comment at the source's original position so we
        // can restore it there when we leave wide mode.
        const comment = document.createComment('split-placeholder');
        source.el.parentNode.insertBefore(comment, source.el);
        this.leftColumn.appendChild(source.el);

        this.splitLayoutPlaceholders.set(source.el, comment);
    }

    unsplitLeftSource(source) {

        const comment = this.splitLayoutPlaceholders.get(source.el);

        if (!comment) {
            return;
        }

        if (comment.parentNode) {
            comment.parentNode.insertBefore(source.el, comment);
            comment.remove();
        }

        this.splitLayoutPlaceholders.delete(source.el);
    }

    /**
     * onDestroy
     * This function is triggered when the view is destroyed.
     * It will then close all open tool tips
     */
    onDestroy() {
        // fixes the duplication of toolTips while navigating through the taskgroups
        if (this.tourToolTip) {
            Backbone.View.layout.unregisterAndDestroyChildView(this.tourToolTip);
        }

        if (this.splitLayoutMediaQuery) {
            this.splitLayoutMediaQuery.removeEventListener('change', this.onChangeSplitLayoutMediaQuery);
        }

        if (this.resizeKnob) {
            this.resizeKnob.removeEventListener('pointerdown', this.onResizeKnobPointerDown);
            this.resizeKnob.removeEventListener('pointermove', this.onResizeKnobPointerMove);
            this.resizeKnob.removeEventListener('pointerup', this.onResizeKnobPointerUp);
            this.resizeKnob.removeEventListener('pointercancel', this.onResizeKnobPointerUp);
        }

        AudioUtil.stopAllAudio()
    }

    /**
     * Navigate to task group at activities/show.
     */
    onClickNavigateToTaskGroup() {
        Backbone.history.navigate(
            '/activities/show/' + this.model.get('parent_id') + '/' + this.model.get('id'), {
                trigger: true
            }
        );
    }

    // Open explanation sidebar using the context of the current task group. Open in
    // side bar overlay if smaller than 1200, or next to content if => 1200px
    showExplanation() {

        if (Backbone.View.sidebar.view instanceof SidebarLearningText) {
            Backbone.View.sidebar.closeSidebar()
            return
        }

        // otherwise show the learning text in the sidebar
        const sidebarLearningTextView = new SidebarLearningText({
            collection: this.model.learningTexts,
            activityModel: this.model.getActivityModel(),
        });
        const showNextToContent = window.innerWidth > 1199 && !this.el.hasAttribute('data-split')
        Backbone.View.sidebar.showSidebar(
            sidebarLearningTextView,
            this.model.getExplanationLabel(),
            Math.max((window.innerWidth - 100) / 2, 600),
            null,
            showNextToContent
        );
        this.registerChildView(sidebarLearningTextView);
    }
}
